// lurik-frontend/src/presentation/pages/authentication/two-factor/TwoFactorVerifyPage.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTwoFactorService } from '../../../../use-cases/authentication/useTwoFactorService';
import { CodeInput } from './components/CodeInput';
import { BackupCodeInput } from './components/BackupCodeInput';
import { useNavigationService } from '../../../../use-cases/navigation';

// ✅ STYLED COMPONENTS (igual ao TwoFactorPage)
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 480px;
  text-align: center;
`;

const Title = styled.h1`
  color: #2c3e50;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  font-size: 16px;
  margin: 0 0 32px 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #7f8c8d;
  font-size: 16px;
  
  &:before {
    content: "⏳";
    margin-right: 12px;
    animation: spin 1s linear infinite;
    font-size: 20px;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export const TwoFactorVerifyPage: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  
  const { completeTwoFactorLogin } = useTwoFactorService();
  const { navigateTo } = useNavigationService();

  const handleVerifyCode = async () => {
    try {
      setLoading(true);
      
      const tempToken = localStorage.getItem('TEMP_TOKEN');
      if (!tempToken) {
        throw new Error('Token temporário não encontrado');
      }
      
      const result = await completeTwoFactorLogin(verificationCode, tempToken, useBackupCode);
      
      if (result) {
        // ✅ LOGIN COMPLETO: salvar token e redirecionar
        localStorage.setItem('ACCESS_TOKEN', result.token);
        localStorage.removeItem('TEMP_TOKEN');
        
        navigateTo({ route: '/workspace' });
      }
    } catch (error) {
      console.error('Erro ao verificar código:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCode = async (code: string) => {
    try {
      setLoading(true);
      
      const tempToken = localStorage.getItem('TEMP_TOKEN');
      if (!tempToken) {
        throw new Error('Token temporário não encontrado');
      }
      
      const result = await completeTwoFactorLogin(code, tempToken, true);
      
      if (result) {
        // ✅ LOGIN COMPLETO: salvar token e redirecionar
        localStorage.setItem('ACCESS_TOKEN', result.token);
        localStorage.removeItem('TEMP_TOKEN');
        
        navigateTo({ route: '/workspace' });
      }
    } catch (error) {
      console.error('Erro ao verificar código de backup:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Card>
          <LoadingSpinner>
            Verificando código...
          </LoadingSpinner>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>Verificação 2FA</Title>
        <Subtitle>Digite o código do seu authenticator para completar o login</Subtitle>

        {!useBackupCode ? (
          <>
            <CodeInput
              value={verificationCode}
              onChange={setVerificationCode}
              onVerify={handleVerifyCode}
              loading={loading}
            />
            
            <button 
              onClick={() => setUseBackupCode(true)}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#3498db', 
                cursor: 'pointer',
                textDecoration: 'underline',
                marginTop: '16px'
              }}
            >
              Usar código de backup
            </button>
          </>
        ) : (
          <>
            <BackupCodeInput
              onVerify={handleBackupCode}
              loading={loading}
            />
            
            <button 
              onClick={() => setUseBackupCode(false)}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#3498db', 
                cursor: 'pointer',
                textDecoration: 'underline',
                marginTop: '16px'
              }}
            >
              Voltar ao authenticator
            </button>
          </>
        )}
      </Card>
    </Container>
  );
};